import type {
  PostFormValues,
  PostStatus,
} from "@circle-react/components/QuickPostV3/data/types";
import { useComposerSpace } from "@circle-react/components/QuickPostV3/hooks/useComposerSpace";
import { useCurrentPostContext } from "@circle-react/contexts/currentPostContext";
import {
  POST_STATUS_SCHEDULED,
  isPublishedPost,
} from "@circle-react/helpers/postHelpers";
import { parseISO } from "date-fns";
import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

export const useScheduledPost = () => {
  const { setValue } = useFormContext<PostFormValues>();
  const { canManageSelectedSpace } = useComposerSpace();
  const { isEdit, record: post } = useCurrentPostContext();
  const status = useWatch<PostFormValues, "status">({ name: "status" });
  const publishedAt = useWatch<PostFormValues, "published_at">({
    name: "published_at",
  });
  const scheduledAt = useMemo(
    () => (publishedAt ? parseISO(publishedAt) : undefined),
    [publishedAt],
  );
  const isScheduled = status === POST_STATUS_SCHEDULED && Boolean(scheduledAt);
  const disabledDays = useMemo(() => ({ before: new Date() }), []);
  const canSchedule = Boolean(
    canManageSelectedSpace && !(isEdit && isPublishedPost(post)),
  );

  const setStatus = useCallback(
    (next: PostStatus) => setValue("status", next, { shouldDirty: true }),
    [setValue],
  );

  const updatePublishedAt = useCallback(
    (date?: Date | null) => {
      setValue("published_at", date?.toISOString() ?? null, {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const onScheduleDateSave = useCallback(
    (date: Date) => {
      updatePublishedAt(date);
      setStatus(POST_STATUS_SCHEDULED);
    },
    [setStatus, updatePublishedAt],
  );

  return {
    canSchedule,
    disabledDays,
    isEdit: Boolean(isEdit),
    isScheduled,
    onScheduleDateSave,
    scheduledAt,
    setStatus,
    status,
    updatePublishedAt,
  };
};
